import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { maritimeApi } from '../api/maritime';
import { EmptyState, ErrorState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { JsonBlock } from '../components/JsonBlock';
import { PayloadViewer } from '../components/PayloadViewer';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { DisabledActionButton } from '../components/DisabledActionButton';
import { useAsyncData } from '../hooks/useAsyncData';
import { usePermission } from '../hooks/usePermission';
import type {
  MaritimeEdifactStandard,
  MaritimeExchangeRecord,
  MaritimeSystemTemplateType,
  PartnerMappingDefinitionRecord,
} from '../types/api';

const standards: MaritimeEdifactStandard[] = ['COPARN', 'COARRI', 'CODECO', 'CUSCAR', 'CUSRES', 'IFTSTA', 'IFTMAN', 'COREOR'];
const systemTypes: MaritimeSystemTemplateType[] = ['PMIS', 'PCS', 'TOS', 'CUSTOMS'];
const sampleMessage = [
  'UNB+UNOA:2+TOS+PCS+260520:1015+INT-001',
  'UNH+MSG-001+CODECO:D:96A:UN',
  'BGM+34+GATE-001+9',
  'TDT+20+VOY-778+VESSEL-ALPHA',
  'LOC+11+TERM-A',
  'EQD+CN+MSKU1234567+22G1',
  'DTM+7:202605201015:203',
  'UNT+7+MSG-001',
  'UNZ+1+INT-001',
].join("'");

export function MaritimeExchangeDashboardPage() {
  const { search } = useLocation();
  const [standardFilter, setStandardFilter] = useState<MaritimeEdifactStandard | ''>('');
  const [systemFilter, setSystemFilter] = useState<MaritimeSystemTemplateType | ''>('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState<'ALL' | 'EXCEPTIONS' | 'ACK' | 'NACK'>('EXCEPTIONS');
  const [selectedExchange, setSelectedExchange] = useState<MaritimeExchangeRecord | null>(null);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const [replayReason, setReplayReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canReplay = usePermission('flow.operations.execute');
  const { data: dashboard, isLoading, error, refresh } = useAsyncData(() => maritimeApi.dashboard(), []);

  const filteredExchanges = useMemo(() => (dashboard?.recentExchanges ?? []).filter((exchange) => {
    if (standardFilter && exchange.standard !== standardFilter) return false;
    if (systemFilter && exchange.systemType !== systemFilter) return false;
    if (partnerFilter && !exchange.partnerCode.toLowerCase().includes(partnerFilter.toLowerCase())) return false;
    if (outcomeFilter === 'ACK' && exchange.acknowledgement.status !== 'ACK') return false;
    if (outcomeFilter === 'NACK' && exchange.acknowledgement.status !== 'NACK') return false;
    if (outcomeFilter === 'EXCEPTIONS' && exchange.acknowledgement.status !== 'NACK' && exchange.delivery.status !== 'FAILED') return false;
    return true;
  }), [dashboard, outcomeFilter, partnerFilter, standardFilter, systemFilter]);

  const deliveryFailures = dashboard?.recentExchanges.filter((exchange) => exchange.delivery.status === 'FAILED').length ?? 0;
  const total = dashboard?.ackCount || dashboard?.nackCount ? dashboard.ackCount + dashboard.nackCount : 0;
  const ackRate = total ? Math.round(((dashboard?.ackCount ?? 0) / total) * 100) : 100;

  async function replayExchange(exchange: MaritimeExchangeRecord, reason: string) {
    setIsSubmitting(true);
    setActionError(null);
    try {
      const [updated] = await maritimeApi.replay([exchange.id], reason.trim());
      setSelectedExchange(updated ?? exchange);
      await refresh();
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Unable to replay maritime exchange.');
      throw caught;
    } finally {
      setIsSubmitting(false);
    }
  }

  function buildReplayState(exchange: MaritimeExchangeRecord, reason: string): ConfirmDialogState {
    return {
      title: 'Replay maritime exchange',
      message: `Reprocess ${exchange.standard} exchange ${exchange.id} after confirming the underlying issue has been corrected.`,
      confirmLabel: 'Request replay',
      risk: 'high',
      requireText: 'REPLAY',
      detailLabel: 'Operator reason',
      detailValue: reason,
      detailPlaceholder: 'What was corrected before replay?',
      detailRequired: true,
      onDetailChange: (value) => { setReplayReason(value); setConfirm(buildReplayState(exchange, value)); },
      onConfirm: () => replayExchange(exchange, reason),
    };
  }

  if (isLoading) return <LoadingState label="Loading maritime operations..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!dashboard) return null;

  return (
    <div className="page-grid maritime-operations">
      <section className="panel maritime-operations-header">
        <div className="panel-header">
          <div><span className="eyebrow">MARITIME OPERATIONS</span><h1>Maritime Monitoring</h1><p>Monitor EDIFACT acknowledgements and delivery exceptions across PCS, PMIS, terminal, and customs partners.</p></div>
          <div className="panel-actions"><button type="button" onClick={() => void refresh()}>Refresh</button><Link className="button-link" to={{ pathname: '/test-lab', search }}>Open EDIFACT Test Lab</Link></div>
        </div>
        {actionError ? <ErrorState message={actionError} /> : null}
        <div className="operations-card-grid maritime-operations-kpis">
          <Kpi label="ACK rate" value={`${ackRate}%`} detail="Percent of processed maritime messages acknowledged." />
          <Kpi label="NACK" value={dashboard.nackCount} detail="Messages rejected by syntax or maritime semantics." />
          <Kpi label="Delivery failures" value={deliveryFailures} detail="Acknowledged messages that did not reach their destination." />
          <Kpi label="Pending delivery" value={dashboard.deliveryPending} detail="Messages still awaiting downstream delivery." />
        </div>
      </section>

      <section className="panel">
        <div className="panel-header"><div><h2>Maritime exchanges</h2><p>{filteredExchanges.length} of {dashboard.recentExchanges.length} recent exchanges shown.</p></div><Link className="button-link" to={{ pathname: '/dlq', search }}>Open Recovery</Link></div>
        <div className="status-chip-row" aria-label="Maritime outcome focus">
          {(['EXCEPTIONS', 'ALL', 'ACK', 'NACK'] as const).map((outcome) => <button type="button" className={`status-chip${outcomeFilter === outcome ? ' active' : ''}`} aria-pressed={outcomeFilter === outcome} key={outcome} onClick={() => setOutcomeFilter(outcome)}>{outcome === 'ALL' ? 'All recent' : outcome === 'EXCEPTIONS' ? 'Needs attention' : outcome}</button>)}
        </div>
        <div className="toolbar toolbar-grid maritime-filter-grid">
          <label className="filter-field"><span>Standard</span><select value={standardFilter} onChange={(event) => setStandardFilter(event.target.value as MaritimeEdifactStandard | '')}><option value="">All standards</option>{standards.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label className="filter-field"><span>System</span><select value={systemFilter} onChange={(event) => setSystemFilter(event.target.value as MaritimeSystemTemplateType | '')}><option value="">All systems</option>{systemTypes.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label className="filter-field"><span>Partner</span><input placeholder="Partner code" value={partnerFilter} onChange={(event) => setPartnerFilter(event.target.value)} /></label>
          <button type="button" className="ghost-button" disabled={!standardFilter && !systemFilter && !partnerFilter && outcomeFilter === 'EXCEPTIONS'} onClick={() => { setStandardFilter(''); setSystemFilter(''); setPartnerFilter(''); setOutcomeFilter('EXCEPTIONS'); }}>Reset filters</button>
        </div>
        {!filteredExchanges.length ? <EmptyState label="No maritime exchanges matched the current filters." /> : (
          <div className="table-scroll maritime-table-scroll"><table className="data-table maritime-operations-table"><thead><tr><th>Exchange</th><th>Partner / system</th><th>Outcome</th><th>Received</th><th>Issue</th><th>Actions</th></tr></thead><tbody>
            {filteredExchanges.map((exchange) => {
              const issue = [...exchange.syntaxIssues, ...exchange.semanticIssues, ...exchange.acknowledgement.issues][0];
              const replayEligible = exchange.acknowledgement.status === 'NACK' || exchange.delivery.status === 'FAILED';
              return <tr key={exchange.id} className={selectedExchange?.id === exchange.id ? 'selected-row' : undefined}>
                <td data-label="Exchange"><strong>{exchange.standard}</strong><small className="execution-secondary" title={exchange.id}>{shortMaritimeId(exchange.parseResult.interchangeReference ?? exchange.id)}</small></td>
                <td data-label="Partner / system"><strong>{exchange.partnerCode}</strong><small className="execution-secondary">{exchange.systemType}</small></td>
                <td data-label="Outcome"><StatusBadge value={exchange.acknowledgement.status} /> <StatusBadge value={exchange.delivery.status} /></td>
                <td data-label="Received" title={new Date(exchange.createdAt).toLocaleString('en-GB')}>{formatMaritimeTime(exchange.createdAt)}</td>
                <td data-label="Issue" className={issue ? 'execution-error' : ''}>{issue?.message ?? (exchange.delivery.status === 'FAILED' ? 'Downstream delivery failed.' : 'No issue reported.')}</td>
                <td data-label="Actions" className="actions-cell maritime-row-actions"><button type="button" className="ghost-button" onClick={() => setSelectedExchange(selectedExchange?.id === exchange.id ? null : exchange)}>{selectedExchange?.id === exchange.id ? 'Close details' : 'View details'}</button>{canReplay && replayEligible ? <button type="button" className="danger-button" disabled={isSubmitting} onClick={() => { setReplayReason(''); setConfirm(buildReplayState(exchange, '')); }}>Replay</button> : null}</td>
              </tr>;
            })}
          </tbody></table></div>
        )}
      </section>

      {selectedExchange ? <section className="panel maritime-exchange-detail"><div className="panel-header"><div><h2>{selectedExchange.standard} exchange details</h2><p>Operational context for the selected exchange.</p></div></div><div className="summary-grid"><div><strong>Interchange reference</strong><span>{selectedExchange.parseResult.interchangeReference ?? 'n/a'}</span></div><div><strong>Message reference</strong><span>{selectedExchange.parseResult.messageReference ?? 'n/a'}</span></div><div><strong>Vessel / voyage</strong><span>{[selectedExchange.audit.vessel, selectedExchange.audit.voyage].filter(Boolean).join(' / ') || 'n/a'}</span></div><div><strong>Delivery attempts</strong><span>{selectedExchange.delivery.attempts}</span></div><div><strong>Last replay</strong><span>{selectedExchange.lastReplay ? `${selectedExchange.lastReplay.reason} (${new Date(selectedExchange.lastReplay.requestedAt).toLocaleString('en-GB')})` : 'None recorded'}</span></div><div><strong>Processed</strong><span>{new Date(selectedExchange.audit.processedAt).toLocaleString('en-GB')}</span></div></div><div className="stack-list maritime-issues">{[...selectedExchange.syntaxIssues, ...selectedExchange.semanticIssues, ...selectedExchange.acknowledgement.issues].map((issue, index) => <div className={`stack-item alert-${issue.severity === 'error' ? 'danger' : 'warning'}`} key={`${issue.code}-${index}`}><strong>{issue.code} · {issue.stage}</strong><span>{issue.message}</span></div>)}{selectedExchange.syntaxIssues.length + selectedExchange.semanticIssues.length + selectedExchange.acknowledgement.issues.length === 0 ? <EmptyState label="No validation issues were recorded." /> : null}</div></section> : null}

      <section className="panel maritime-tool-links"><div className="panel-header"><div><h2>Engineering tools</h2><p>Use focused workspaces for message simulation and configuration.</p></div></div><div className="panel-actions"><Link className="button-link" to={{ pathname: '/test-lab', search }}>EDIFACT Test Lab</Link><Link className="button-link" to={{ pathname: '/mapping-designer', search }}>Mapping Designer</Link><Link className="button-link" to={{ pathname: '/connectors', search }}>Delivery channels</Link></div></section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function LegacyMaritimeExchangeDashboardPage() {
  const [standard, setStandard] = useState<MaritimeEdifactStandard>('CODECO');
  const [systemType, setSystemType] = useState<MaritimeSystemTemplateType>('TOS');
  const [partnerCode, setPartnerCode] = useState('TOS');
  const [message, setMessage] = useState(`${sampleMessage}'`);
  const [selectedExchange, setSelectedExchange] = useState<MaritimeExchangeRecord | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const { data: dashboard, isLoading, error, refresh } = useAsyncData(() => maritimeApi.dashboard(), []);
  const { data: mappingData } = useAsyncData(() => maritimeApi.mappings(), []);
  const mappings = mappingData ?? [];

  const activeExchange = selectedExchange ?? dashboard?.recentExchanges[0] ?? null;
  const activeMapping = useMemo(
    () => mappings.find((mapping) => mapping.standard === standard && (mapping.partnerCode === systemType || mapping.partnerCode === partnerCode)) ?? mappings[0],
    [mappings, partnerCode, standard, systemType],
  );

  async function processMessage() {
    setIsSubmitting(true);
    setActionError(null);

    try {
      const exchange = await maritimeApi.process({
        standard,
        partnerCode,
        systemType,
        message,
        mappingId: activeMapping?.id,
      });
      setSelectedExchange(exchange);
      await refresh();
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Unable to process maritime message.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function replayActive() {
    if (!activeExchange) return;
    setIsSubmitting(true);
    setActionError(null);

    try {
      const [exchange] = await maritimeApi.replay([activeExchange.id], 'Legacy maritime dashboard replay');
      setSelectedExchange(exchange ?? activeExchange);
      await refresh();
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Unable to replay maritime exchange.');
    } finally {
      setIsSubmitting(false);
    }
  }

  function replayActiveDisabledReason(): string | null {
    if (isSubmitting) return 'A maritime operation is already running.';
    if (!activeExchange) return 'Select an exchange to replay.';
    return null;
  }

  function requestReplayActive() {
    if (replayActiveDisabledReason() || !activeExchange) return;
    setConfirm({
      title: 'Replay maritime exchange',
      message: `This resubmits ${activeExchange.standard} exchange ${activeExchange.id} for reprocessing.`,
      confirmLabel: 'Replay active',
      risk: 'high',
      requireText: 'REPLAY',
      onConfirm: replayActive,
    });
  }

  if (isLoading) return <LoadingState label="Loading maritime exchange dashboard..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!dashboard) return null;

  return (
    <div className="page-grid">
      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Maritime exchange dashboard</h2>
            <p>EDIFACT middleware cockpit for PCS, PMIS, TOS, and customs interoperability.</p>
          </div>
          <div className="panel-actions">
            <button type="button" onClick={() => void refresh()}>Refresh</button>
            <DisabledActionButton reason={replayActiveDisabledReason()} onClick={requestReplayActive}>Replay active</DisabledActionButton>
          </div>
        </div>
        {actionError ? <ErrorState message={actionError} /> : null}
        <div className="operations-card-grid maritime-kpi-grid">
          <Kpi label="Supported standards" value={dashboard.supportedStandards.length} detail={dashboard.supportedStandards.join(', ')} />
          <Kpi label="ACK" value={dashboard.ackCount} detail="Functional acknowledgements accepted by validation." />
          <Kpi label="NACK" value={dashboard.nackCount} detail="Messages rejected by syntax or maritime semantics." />
          <Kpi label="Batch ceiling" value={dashboard.highVolume.maxMessagesPerBatch} detail={`${dashboard.highVolume.batchSize} message replay batches.`} />
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Recent exchanges</h2>
            <p>Select an exchange to inspect and replay. {dashboard.recentExchanges.length} available.</p>
          </div>
        </div>
        {dashboard.recentExchanges.length === 0 ? <EmptyState label="No maritime exchanges processed yet." /> : (
          <table className="data-table">
            <thead><tr><th>Standard</th><th>Partner</th><th>Status</th><th>Delivery</th><th /></tr></thead>
            <tbody>
              {dashboard.recentExchanges.map((exchange) => (
                <tr key={exchange.id} className={activeExchange?.id === exchange.id ? 'selected-row' : undefined}>
                  <td>{exchange.standard}</td>
                  <td className="mono">{exchange.partnerCode}</td>
                  <td><StatusBadge value={exchange.acknowledgement.status} /></td>
                  <td><StatusBadge value={exchange.delivery.status} /></td>
                  <td>
                    <button type="button" disabled={activeExchange?.id === exchange.id} onClick={() => setSelectedExchange(exchange)}>
                      {activeExchange?.id === exchange.id ? 'Selected' : 'Inspect'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="page-grid two-columns">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Message inspector</h2>
              <p>Run parse, syntax validation, semantic validation, acknowledgement, and transformation.</p>
            </div>
          </div>
          <div className="form-grid">
            <label>
              Standard
              <select value={standard} onChange={(event) => setStandard(event.target.value as MaritimeEdifactStandard)}>
                {standards.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <label>
              System
              <select value={systemType} onChange={(event) => setSystemType(event.target.value as MaritimeSystemTemplateType)}>
                {systemTypes.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <label>
              Partner
              <input value={partnerCode} onChange={(event) => setPartnerCode(event.target.value)} />
            </label>
            <label>
              Mapping
              <select value={activeMapping?.id ?? ''} disabled>
                {mappings.map((mapping) => <option key={mapping.id} value={mapping.id}>{mapping.name}</option>)}
              </select>
            </label>
            <label className="full-width">
              EDIFACT
              <textarea value={message} rows={9} onChange={(event) => setMessage(event.target.value)} />
            </label>
          </div>
          <div className="form-actions">
            <button type="button" disabled={isSubmitting} onClick={() => void processMessage()}>Process message</button>
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Integration templates</h2>
              <p>Connectivity defaults for PMIS, PCS, terminal, and customs systems.</p>
            </div>
          </div>
          <div className="stack-list">
            {dashboard.templates.map((template) => (
              <div className="stack-item" key={template.id}>
                <strong>{template.type} - {template.name}</strong>
                <span>{template.supportedStandards.join(', ')}</span>
                <span>{template.connectorProfiles.map((profile) => profile.protocol).join(' / ')}</span>
              </div>
            ))}
          </div>
        </section>
      </section>

      <section className="page-grid two-columns">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>EDIFACT viewer</h2>
              <p>Segment-level rendering of the selected maritime exchange.</p>
            </div>
          </div>
          {!activeExchange ? <EmptyState label="Process a message to inspect EDIFACT segments." /> : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Tag</th>
                  <th>Raw segment</th>
                </tr>
              </thead>
              <tbody>
                {activeExchange.parseResult.segments.map((segment) => (
                  <tr key={`${segment.position}-${segment.raw}`}>
                    <td>{segment.position}</td>
                    <td><strong>{segment.tag}</strong></td>
                    <td className="mono">{segment.raw}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Mapping debugger</h2>
              <p>Effective mapping with inheritance and custom override visibility.</p>
            </div>
          </div>
          <MappingDebugger mapping={activeMapping} />
        </section>
      </section>

      {activeExchange ? (
        <section className="page-grid two-columns">
          <section className="panel">
            <div className="panel-header">
              <div>
                <h2>Acknowledgement management</h2>
                <p>ACK/NACK status, functional acknowledgement body, and delivery state.</p>
              </div>
              <StatusBadge value={activeExchange.acknowledgement.status} />
            </div>
            <div className="summary-grid">
              <div><strong>Functional code</strong><span>{activeExchange.acknowledgement.functionalCode}</span></div>
              <div><strong>Delivery</strong><span><StatusBadge value={activeExchange.delivery.status} /></span></div>
              <div><strong>Attempts</strong><span>{activeExchange.delivery.attempts}</span></div>
              <div><strong>Generated</strong><span>{new Date(activeExchange.acknowledgement.generatedAt).toLocaleString('en-GB')}</span></div>
            </div>
            <PayloadViewer value={activeExchange.acknowledgement.body} contentType="text/plain" filename={`${activeExchange.id}.ack.EDI`} />
          </section>
          <section className="panel">
            <div className="panel-header">
              <div>
                <h2>Maritime audit</h2>
                <p>Vessel, voyage, escale, partner, terminal, and timestamps.</p>
              </div>
            </div>
            <JsonBlock value={activeExchange.audit} />
          </section>
        </section>
      ) : null}

      {activeExchange ? (
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Transformation output</h2>
              <p>Canonical payload ready for downstream PMIS, PCS, terminal, or customs adapters.</p>
            </div>
          </div>
          <PayloadViewer value={activeExchange.transformedPayload} filename={`${activeExchange.id}.transformed-payload.json`} />
        </section>
      ) : null}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function shortMaritimeId(value: string): string {
  return value.length > 34 ? `${value.slice(0, 34)}...` : value;
}

function formatMaritimeTime(value: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60_000));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function Kpi({ label, value, detail }: { label: string; value: string | number; detail: string }) {
  return (
    <article className="operations-card">
      <span className="operations-card-label">{label}</span>
      <strong>{value}</strong>
      <p>{detail}</p>
    </article>
  );
}

function MappingDebugger({ mapping }: { mapping?: PartnerMappingDefinitionRecord }) {
  if (!mapping) {
    return <EmptyState label="No maritime mappings are available." />;
  }

  return (
    <div className="stack-list">
      <div className="stack-item">
        <strong>{mapping.name}</strong>
        <span>{mapping.standard} for {mapping.partnerCode}</span>
        <span>Inherits from {mapping.inheritsFrom ?? 'base maritime mapping'}</span>
      </div>
      <JsonBlock value={{ overrides: mapping.overrides, effectiveMapping: mapping.effectiveMapping }} />
    </div>
  );
}
